import * as d3 from 'd3';
import {
	hasMovedNoticably,
	hexOrNamedColour,
	removeEl,
	removeNoDeleteAnimation,
} from './graphUtils';
import { GraphProps } from '../../types';
import { NCLink } from '../../../../src/commonTypes';
import {
	getSynthesiseRecipeFlowEndpoints,
	isSynthesiseRecipeLink,
} from './synthesiseRecipeLink';

const enterLink = (
	graphProps: GraphProps,
	elem: d3.Selection<d3.EnterElement, NCLink, SVGGElement, unknown>
): d3.Selection<SVGGElement, NCLink, SVGGElement, unknown> => {
	const link = elem.append('g').attr('class', 'link');
	link.append('line').attr('class', 'link-holder');
	link.append('line').attr('class', 'link-line');
	link.append('line').attr('class', 'link-flow');
	link.append('polygon').attr('class', 'input-output-arrow');
	link
		.append('circle')
		.attr('r', 24)
		.attr('class', 'link-delete-circle')
		.append('title')
		.text('Delete connection');
	link
		.append('image')
		.attr('class', 'delete-link')
		.attr('width', 20)
		.attr('height', 20)
		.attr('href', `/assets/graph/delete.svg`);
	updateLink(graphProps, link);
	return link;
};

const getCenterX = (d: NCLink) => d.source.x + (d.target.x - d.source.x) / 2;
const getCenterY = (d: NCLink) => d.source.y + (d.target.y - d.source.y) / 2;
const getDeleteX = (d: NCLink) => d.source.x + (d.target.x - d.source.x) / 2 - 10;
const getDeleteY = (d: NCLink) => d.source.y + (d.target.y - d.source.y) / 2 - 10;

/**
 * Stored links predate directional rendering and generated nodes are sometimes
 * persisted as child -> parent. Orient the shimmer semantically while leaving
 * the stored link untouched for selection, deletion and collaboration.
 */
const getFlowEndpoints = (link: NCLink): { from: NCLink['source']; to: NCLink['target'] } => {
	const recipeFlow = getSynthesiseRecipeFlowEndpoints(link);
	if (recipeFlow) return recipeFlow;

	const sourceIsChild = link.source.parent?.uid === link.target.uid;
	const targetIsChild = link.target.parent?.uid === link.source.uid;
	if (sourceIsChild && !targetIsChild) {
		return { from: link.target, to: link.source };
	}
	return { from: link.source, to: link.target };
};

const getLinkColourWithOpacity = (d: NCLink) => {
	const baseColor = d.colour ? hexOrNamedColour(d.colour) : '#91909A';
	
	// Handle CSS variable references
	if (baseColor.startsWith('var(')) {
		const varName = baseColor.slice(4, -1); // Remove 'var(' and ')'
		
		// Create a temporary element to get the computed color
		const tempDiv = document.createElement('div');
		tempDiv.style.color = `var(${varName})`;
		document.body.appendChild(tempDiv);
		const computedColor = getComputedStyle(tempDiv).color;
		document.body.removeChild(tempDiv);
		
		// Parse the computed RGB color
		const rgbMatch = computedColor.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
		if (rgbMatch) {
			const [, r, g, b] = rgbMatch;
			const opacity = Math.round(0.35 * 255).toString(16).padStart(2, '0');
			return `#${parseInt(r).toString(16).padStart(2, '0')}${parseInt(g).toString(16).padStart(2, '0')}${parseInt(b).toString(16).padStart(2, '0')}${opacity}`;
		}
	}
	
	// Convert hex to rgba, add opacity, then convert back to hex
	const r = parseInt(baseColor.slice(1, 3), 16);
	const g = parseInt(baseColor.slice(3, 5), 16);
	const b = parseInt(baseColor.slice(5, 7), 16);
	const opacity = Math.round(0.7 * 255).toString(16).padStart(2, '0');
	return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}${opacity}`;
};

export const updateLinkPositions = (
	links: d3.Selection<SVGGElement, NCLink, SVGGElement, unknown>
): d3.Selection<SVGGElement, NCLink, SVGGElement, unknown> => {
	links.each(function (d) {
		const linkHolder = this.querySelector<SVGLineElement>('.link-holder');
		const linkLine = this.querySelector<SVGLineElement>('.link-line');
		const linkFlow = this.querySelector<SVGLineElement>('.link-flow');
		const inputOutputArrow = this.querySelector<SVGPolygonElement>('.input-output-arrow');

		if (linkHolder && linkLine && linkFlow) {
			const flow = getFlowEndpoints(d);
			linkFlow.setAttribute('x1', String(flow.from.x));
			linkFlow.setAttribute('y1', String(flow.from.y));
			linkFlow.setAttribute('x2', String(flow.to.x));
			linkFlow.setAttribute('y2', String(flow.to.y));

			if (inputOutputArrow) {
				const isRecipeLink = isSynthesiseRecipeLink(d);
				const dx = flow.to.x - flow.from.x;
				const dy = flow.to.y - flow.from.y;
				const length = Math.hypot(dx, dy);
				inputOutputArrow.classList.toggle('visible', isRecipeLink && length > 0);
				if (isRecipeLink && length > 0) {
					const unitX = dx / length;
					const unitY = dy / length;
					const centreX = flow.from.x + dx * 0.68;
					const centreY = flow.from.y + dy * 0.68;
					const tipX = centreX + unitX * 9;
					const tipY = centreY + unitY * 9;
					const baseX = centreX - unitX * 7;
					const baseY = centreY - unitY * 7;
					const perpendicularX = -unitY * 6;
					const perpendicularY = unitX * 6;
					inputOutputArrow.setAttribute(
						'points',
						`${tipX},${tipY} ${baseX + perpendicularX},${baseY + perpendicularY} ${
							baseX - perpendicularX
						},${baseY - perpendicularY}`
					);
				}
			}

			const sourceOldPos = {
				x: linkHolder.x1.baseVal.value || 0,
				y: linkHolder.y1.baseVal.value || 0,
			};
			const targetOldPos = {
				x: linkHolder.x2.baseVal.value || 0,
				y: linkHolder.y2.baseVal.value || 0,
			};

			const sourceMoved = hasMovedNoticably(sourceOldPos, d.source);
			if (sourceMoved) {
				linkHolder.setAttribute('x1', String(d.source.x));
				linkHolder.setAttribute('y1', String(d.source.y));
				linkLine.setAttribute('x1', String(d.source.x));
				linkLine.setAttribute('y1', String(d.source.y));
			}

			const targetMoved = hasMovedNoticably(targetOldPos, d.target);
			if (targetMoved) {
				linkHolder.setAttribute('x2', String(d.target.x));
				linkHolder.setAttribute('y2', String(d.target.y));
				linkLine.setAttribute('x2', String(d.target.x));
				linkLine.setAttribute('y2', String(d.target.y));
			}

			if (sourceMoved || targetMoved) {
				const deleteCircle = this.querySelector<SVGCircleElement>('.link-delete-circle');
				const deleteLink = this.querySelector<SVGImageElement>('.delete-link');
				if (deleteCircle && deleteLink) {
					deleteCircle.setAttribute('cx', String(getCenterX(d)));
					deleteCircle.setAttribute('cy', String(getCenterY(d)));
					deleteLink.setAttribute('x', String(getDeleteX(d)));
					deleteLink.setAttribute('y', String(getDeleteY(d)));
				}
			}
		}
	});

	return links;
};

export const updateActiveLink = (
	links: d3.Selection<SVGGElement, NCLink, SVGGElement, unknown>,
	graphProps: GraphProps
): d3.Selection<SVGGElement, NCLink, SVGGElement, unknown> => {
	// Update active class
	links.filter('.active').classed('active', false);
	if (graphProps.activeLink) {
		links
			.filter(
				(d) =>
					graphProps.activeLink?.source === d.source.uid &&
					graphProps.activeLink?.target === d.target.uid
			)
			.classed('active', true);
	}
	return links;
};

const updateLink = (
	graphProps: GraphProps,
	elem: d3.Selection<SVGGElement, NCLink, SVGGElement, unknown>
): d3.Selection<SVGGElement, NCLink, SVGGElement, unknown> => {
	const updatedLink = elem;

	updateActiveLink(updatedLink, graphProps);

	// Remove hidden class from links where source and target are not hidden
	updatedLink
		.filter('.hidden')
		.filter((d) => Boolean(!d.source.hidden && !d.target.hidden))
		.classed('hidden', false);
	// Add hidden class to links where source or target is hidden
	updatedLink.filter((d) => Boolean(d.source.hidden || d.target.hidden)).classed('hidden', true);
	updatedLink.classed('synthesise-recipe-link', isSynthesiseRecipeLink);

	updatedLink
		.selectAll<SVGLineElement, NCLink>('.link-line')
		.attr('stroke', graphProps.areLinksColoured ? getLinkColourWithOpacity : '#91909A');

	updateLinkPositions(updatedLink);

	return updatedLink;
};

const linkKeyFn = (d: NCLink): string => `${d.source.uid}-${d.target.uid}`;

/**
 * Create line elements inside the group with class '.links'.
 */
export const createLinkLines = (
	linksGroup: d3.Selection<SVGGElement, NCLink, SVGGElement, unknown>,
	links: Array<NCLink>,
	graphProps: GraphProps
) => {
	return linksGroup.data<NCLink>(links, linkKeyFn).join<SVGGElement, NCLink>(
		enterLink.bind(null, graphProps),
		(
			update: d3.Selection<SVGGElement, NCLink, SVGGElement, unknown>
		): d3.Selection<SVGGElement, NCLink, SVGGElement, unknown> => {
			return updateLink(graphProps, update);
		},
		graphProps.isDeleteAnimated ? removeEl : removeNoDeleteAnimation
	);
};

export const updateLinkLines = (
	linksGroup: d3.Selection<SVGGElement, NCLink, SVGGElement, unknown>,
	graphProps: GraphProps
) => {
	return updateLink(graphProps, linksGroup);
};
